import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { TextInput, Button, Card, Title, Paragraph, useTheme, ActivityIndicator, Portal, Snackbar } from 'react-native-paper';
import { supabase } from '../../services/supabase';

type Props = {
  navigation: any;
};

type ConfigEvolution = {
  id?: number;
  url: string;
  apikey: string;
};

export function ConfiguracaoEvolutionAPI({ navigation }: Props) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [config, setConfig] = useState<ConfigEvolution>({
    url: '',
    apikey: '',
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      
      // Verificar se o usuário é admin
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data: userData, error: userError } = await supabase
        .from('usuarios')
        .select('admin')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      if (!userData?.admin) throw new Error('Acesso negado. Você não tem permissão de administrador.');

      console.log('Buscando configuração da Evolution API...');
      
      // Buscar configuração atual - sem filtro de codempresa para pegar a configuração padrão
      const { data, error } = await supabase
        .from('config_geral')
        .select('*')
        .eq('nome_integracao', 'evolutionapi')
        .is('codempresa', null) // Buscar apenas configurações sem empresa (configuração global)
        .limit(1);

      if (error) {
        console.error('Erro ao buscar configuração:', error);
      }

      console.log('Resultado da busca:', data);

      // Se encontrou dados
      if (data && data.length > 0 && data[0].dados) {
        // Extrair os dados do campo JSONB
        const configData = data[0].dados;
        setConfig({
          id: data[0].id,
          url: configData.url || '',
          apikey: configData.apikey || ''
        });
        console.log('Configuração carregada:', configData);
      } else {
        // Tentar buscar sem o filtro is('codempresa', null) caso não encontre
        const { data: dataAlt, error: errorAlt } = await supabase
          .from('config_geral')
          .select('*')
          .eq('nome_integracao', 'evolutionapi')
          .limit(1);
          
        if (errorAlt) {
          console.error('Erro na busca alternativa:', errorAlt);
        } else if (dataAlt && dataAlt.length > 0 && dataAlt[0].dados) {
          const configData = dataAlt[0].dados;
          setConfig({
            id: dataAlt[0].id,
            url: configData.url || '',
            apikey: configData.apikey || ''
          });
          console.log('Configuração alternativa carregada:', configData);
        } else {
          console.log('Nenhuma configuração encontrada');
        }
      }
    } catch (err: any) {
      console.error('Erro completo:', err);
      setError(err.message || 'Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      setSaving(true);
      
      if (!config.url || !config.apikey) {
        setError('Preencha todos os campos');
        return;
      }

      // Verificar se o usuário é admin
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data: userData, error: userError } = await supabase
        .from('usuarios')
        .select('admin')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      if (!userData?.admin) throw new Error('Acesso negado. Você não tem permissão de administrador.');

      // Preparar os dados para salvar no campo JSONB
      const dadosConfig = {
        url: config.url,
        apikey: config.apikey
      };
      
      let result;
      
      if (config.id) {
        // Atualizar configuração existente
        result = await supabase
          .from('config_geral')
          .update({
            dados: dadosConfig,
            nome_integracao: 'evolutionapi',
            codempresa: null // Configuração geral não tem codempresa
          })
          .eq('id', config.id);
      } else {
        // Criar nova configuração
        result = await supabase
          .from('config_geral')
          .insert({
            dados: dadosConfig,
            nome_integracao: 'evolutionapi',
            codempresa: null // Configuração geral não tem codempresa
          });
      }

      if (result.error) {
        console.error('Erro ao salvar:', result.error);
        throw result.error;
      }
      
      setSuccess('Configuração salva com sucesso!');
      console.log('Configuração salva com sucesso');
      
      // Recarregar os dados após salvar
      loadConfig();
    } catch (err: any) {
      console.error('Erro ao salvar:', err);
      setError(err.message || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    // Verificar campos antes de iniciar o loading
    if (!config.url || !config.apikey) {
      setError('Preencha todos os campos para testar a conexão');
      return;
    }

    try {
      setLoading(true);
      
      // Aqui você implementaria a lógica para testar a conexão com a Evolution API
      // Por exemplo, fazendo uma requisição para verificar se a API está respondendo
      
      // Simulando um teste de conexão bem-sucedido usando Promise para melhor controle
      await new Promise<void>((resolve) => {
        setTimeout(() => {
          setSuccess('Conexão com a Evolution API estabelecida com sucesso!');
          setLoading(false);
          resolve();
        }, 1500);
      });
      
    } catch (err: any) {
      setError(err.message || 'Erro ao testar conexão');
      setLoading(false);
    }
  };

  if (loading && !config.url) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView>
        <Title style={styles.title}>Configuração da Evolution API</Title>
        <Paragraph style={styles.subtitle}>Configure a integração com o WhatsApp</Paragraph>

        <Card style={styles.card}>
          <Card.Content>
            <TextInput
              label="URL da Evolution API"
              value={config.url}
              onChangeText={(text) => setConfig(prev => ({ ...prev, url: text }))}
              mode="outlined"
              style={styles.input}
              placeholder="https://sua-evolution-api.com"
            />

            <TextInput
              label="API Key"
              value={config.apikey}
              onChangeText={(text) => setConfig(prev => ({ ...prev, apikey: text }))}
              mode="outlined"
              style={styles.input}
              secureTextEntry
              placeholder="Sua chave de API"
            />

            <View style={styles.buttonContainer}>
              <Button
                mode="contained"
                onPress={saveConfig}
                style={styles.button}
                loading={saving}
                disabled={loading || saving}
              >
                Salvar Configuração
              </Button>

              <Button
                mode="outlined"
                onPress={testConnection}
                style={styles.button}
                loading={loading && !saving}
                disabled={loading || saving || !config.url || !config.apikey}
              >
                Testar Conexão
              </Button>
            </View>
          </Card.Content>
        </Card>

        <Card style={styles.infoCard}>
          <Card.Content>
            <Title>Informações</Title>
            <Paragraph>
              A Evolution API é uma API para integração com o WhatsApp que permite enviar mensagens
              automatizadas. Para configurar, você precisa fornecer a URL da sua instância da Evolution API
              e a chave de API para autenticação.
            </Paragraph>
          </Card.Content>
        </Card>
      </ScrollView>

      <Portal>
        <Snackbar
          visible={!!error}
          onDismiss={() => setError(null)}
          duration={3000}
          style={{ backgroundColor: '#F44336' }}
        >
          {error}
        </Snackbar>

        <Snackbar
          visible={!!success}
          onDismiss={() => setSuccess(null)}
          duration={3000}
          style={{ backgroundColor: '#4CAF50' }}
        >
          {success}
        </Snackbar>
      </Portal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 24,
    textAlign: 'center',
    color: '#666',
  },
  card: {
    marginBottom: 16,
    elevation: 4,
  },
  infoCard: {
    marginBottom: 16,
    backgroundColor: '#e3f2fd',
  },
  input: {
    marginBottom: 16,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  button: {
    flex: 1,
    marginHorizontal: 4,
  },
});
